import React, { useEffect, useLayoutEffect, useState } from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import {
  MAX_PREVIEW_ROWS,
  MAX_WORKBOOK_BYTES,
  type XlsxPreviewSheetModel,
} from "../helpers/xlsxSheetPreviewParse";

export interface XlsxViewerProps {
  /** Blob URL or http(s) URL to the workbook */
  source: string;
  fileName?: string;
}

type SheetModel = XlsxPreviewSheetModel;

function formatMaxFileSizeMb(bytes: number): string {
  return (bytes / (1024 * 1024)).toFixed(0);
}

const XlsxViewer: React.FC<XlsxViewerProps> = ({ source, fileName }) => {
  const [error, setError] = useState<string | null>(null);
  const [sheets, setSheets] = useState<SheetModel[]>([]);
  const [tab, setTab] = useState(0);
  const [parsing, setParsing] = useState(false);

  useLayoutEffect(() => {
    if (!source) {
      setParsing(false);
      setError(null);
      setSheets([]);
      return;
    }
    setParsing(true);
    setError(null);
    setSheets([]);
    setTab(0);
  }, [source, fileName]);

  useEffect(() => {
    if (!source) {
      return;
    }

    let cancelled = false;
    let worker: Worker | undefined;

    (async () => {
      try {
        const isHttpUrl =
          source.startsWith("http://") || source.startsWith("https://");
        const res = await fetch(source, {
          ...(isHttpUrl
            ? {
                credentials: "include" as RequestCredentials,
                mode: "cors" as RequestMode,
              }
            : {}),
        });
        if (!res.ok) {
          throw new Error(`Failed to load workbook (${res.status})`);
        }
        const buf = await res.arrayBuffer();
        if (cancelled) return;

        if (buf.byteLength > MAX_WORKBOOK_BYTES) {
          setError(
            `This workbook is too large to preview (max ${formatMaxFileSizeMb(
              MAX_WORKBOOK_BYTES,
            )} MB).`,
          );
          setParsing(false);
          return;
        }

        worker = new Worker(new URL("./xlsxPreview.worker.ts", import.meta.url), {
          type: "module",
        });

        worker.onmessage = (ev: MessageEvent<{ ok: boolean; sheets?: SheetModel[]; message?: string }>) => {
          if (cancelled) return;
          const data = ev.data;
          if (data.ok && data.sheets) {
            setSheets(data.sheets);
            setError(null);
          } else {
            setSheets([]);
            setError(
              typeof data.message === "string"
                ? data.message
                : "Could not read spreadsheet.",
            );
          }
          setParsing(false);
          worker?.terminate();
          worker = undefined;
        };

        worker.onerror = () => {
          if (cancelled) return;
          setSheets([]);
          setError("Could not read spreadsheet.");
          setParsing(false);
          worker?.terminate();
          worker = undefined;
        };

        worker.postMessage({ buffer: buf }, [buf]);
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Could not read spreadsheet.",
          );
          setParsing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      worker?.terminate();
    };
  }, [source, fileName]);

  if (!source) return null;

  if (error) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        {error}
      </div>
    );
  }

  if (parsing) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        Loading spreadsheet…
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="flex-1 min-h-0 overflow-auto p-6 text-center text-md text-hex-gray-600 border-t border-hex-gray-100">
        This workbook has no sheets to display.
      </div>
    );
  }

  const active = sheets[tab] ?? sheets[0];
  const colCount = active.colCount;

  return (
    <div className="flex-1 min-h-0 flex flex-col border-t border-hex-gray-100 text-hex-gray-800">
      {sheets.length > 1 && (
        <Box
          sx={{ borderBottom: 1, borderColor: "divider", px: 1, flexShrink: 0 }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="Workbook sheets"
          >
            {sheets.map((s, i) => (
              <Tab key={`${i}-${s.name}`} label={s.name || `Sheet ${i + 1}`} />
            ))}
          </Tabs>
        </Box>
      )}
      <div className="min-h-0 flex-1 overflow-auto p-2">
        {(active.truncated || active.columnsTruncated) && (
          <div className="text-sm text-hex-gray-600 mb-2 px-1 space-y-1">
            {active.truncated && (
              <p>Showing the first {MAX_PREVIEW_ROWS.toLocaleString()} rows only.</p>
            )}
            {active.columnsTruncated && (
              <p>
                Showing the first {active.colCount.toLocaleString()} of{" "}
                {active.fullColCount.toLocaleString()} columns (preview limit).
              </p>
            )}
          </div>
        )}
        <div className="inline-block min-w-full rounded border border-hex-gray-200 bg-white shadow-xs">
          <table
            style={active.baseTableTypography}
            className={`w-full border-collapse text-sm ${
              active.hasColWidths ? "table-fixed" : ""
            }`}
            aria-label={
              fileName
                ? `Preview: ${fileName} — ${active.name}`
                : `Sheet ${active.name}`
            }
          >
            {active.colWidthsPx.length > 0 && (
              <colgroup>
                {active.colWidthsPx.map((w, i) => (
                  <col
                    key={i}
                    style={
                      w != null ? { width: w, minWidth: w } : undefined
                    }
                  />
                ))}
              </colgroup>
            )}
            <tbody>
              {active.rows.length === 0 ? (
                <tr>
                  <td className="px-2 py-1.5 text-hex-gray-500" colSpan={colCount}>
                    Empty sheet
                  </td>
                </tr>
              ) : (
                active.rows.map((row, ri) => (
                  <tr
                    key={ri}
                    style={
                      active.rowHeightsPx[ri] != null
                        ? {
                            height: active.rowHeightsPx[ri],
                            minHeight: active.rowHeightsPx[ri],
                          }
                        : undefined
                    }
                  >
                    {Array.from({ length: colCount }, (_, ci) => {
                      const cell = row[ci];
                      const nowrap =
                        cell?.style?.whiteSpace !== "normal" &&
                        cell?.style?.whiteSpace !== "pre-wrap";
                      return (
                        <td
                          key={ci}
                          className={`px-2 py-1.5 align-top min-w-0 border border-hex-gray-200 ${
                            nowrap
                              ? "whitespace-nowrap"
                              : "whitespace-normal wrap-break-word"
                          }`}
                          style={cell?.style}
                        >
                          {cell?.text ?? ""}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default XlsxViewer;
